import { React, ts } from '_core';

import { loadAndCreateAnalyses as loadAndCreateAnalysesProcedure } from './procedures';
import { validateAnalysisConfig } from './procedures';
import { AnalysesContextTypes } from './types';

type ProviderProps<T extends string> = {
  config: ts.types.analysis.AnalysisConfig<T>;
  resourceId: number | null;
  resourceType: ts.types.common.Resource['item_type'];
  children: React.ReactNode;
};

const AnalysesContext = React.createContext<AnalysesContextTypes<any> | null>(null);
const AnalysesContextProvider = <T extends string, TResource = any>({
  config,
  resourceId,
  resourceType,
  children,
}: ProviderProps<T>) => {
  React.useEffect(() => {
    validateAnalysisConfig(config);
  }, [config]);

  const [reducedAnalyses, setReducedAnalyses] = React.useState<ts.types.analysis.AnalysisRef<T>[] | null>(null);
  const [loadingAnalyses, setLoadingAnalyses] = React.useState(false);

  const loadAndCreateAnalyses = React.useCallback(
    async (
      isSharedResource: boolean,
      resource: TResource,
      onError?: (_error: Error | unknown) => void,
      opts?: { presetKey?: string | null }
    ): Promise<ts.types.analysis.AnalysisRef<T>[]> => {
      if (!resourceId || !resource) return [];
      setLoadingAnalyses(true);
      setReducedAnalyses(null);
      try {
        const analyses = await loadAndCreateAnalysesProcedure(
          config,
          resourceId,
          resourceType,
          isSharedResource,
          resource,
          opts
        );
        setReducedAnalyses(analyses);
        return analyses;
      } catch (err) {
        if (onError) {
          onError(err);
        }
        setReducedAnalyses([]);
        throw err;
      } finally {
        setLoadingAnalyses(false);
      }
    },
    [config, resourceId, resourceType]
  );

  React.useEffect(
    () =>
      function cleanUp() {
        setReducedAnalyses(null);
        setLoadingAnalyses(false);
      },
    [resourceId]
  );

  const contextValue = React.useMemo(
    () => ({
      reducedAnalyses,
      setReducedAnalyses,
      loadingAnalyses,
      loadAndCreateAnalyses,
      resourceType,
      resourceId,
      config,
    }),
    [reducedAnalyses, setReducedAnalyses, loadingAnalyses, loadAndCreateAnalyses, resourceType, resourceId, config]
  );

  return <AnalysesContext.Provider value={contextValue}>{children}</AnalysesContext.Provider>;
};

AnalysesContextProvider.displayName = 'AnalysesContextProvider';

export { AnalysesContext, AnalysesContextProvider };
export const useAnalysesContext = <T extends string, TResource = any>(): AnalysesContextTypes<T, TResource> => {
  const context = React.useContext(AnalysesContext);
  return context as AnalysesContextTypes<T, TResource>;
};
